import { useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '@skolveckan/shared';
import { api, streamChat } from '../lib/api';
import { IconChat, IconSend } from './Icons';

interface Props {
  lessonId: string;
  enabled: boolean;
}

export function LessonChat({ lessonId, enabled }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  // Svaret som strömmar in just nu. Egen state i stället för en rad i
  // messages, så att en avbruten ström inte lämnar en halv bubbla kvar.
  const [streaming, setStreaming] = useState('');
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api
      .chat(lessonId)
      .then((view) => {
        if (cancelled) return;
        setMessages(view.messages);
        setSuggestions(view.suggestions);
      })
      .catch(() => {
        if (!cancelled) setError('Chatten kunde inte laddas.');
      });
    return () => {
      cancelled = true;
    };
  }, [lessonId, enabled]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, sending, streaming]);

  if (!enabled) return null;

  async function send(text: string) {
    const message = text.trim();
    if (!message || sending) return;

    setSending(true);
    setError(null);
    setDraft('');
    setStreaming('');

    // Optimistiskt: elevens rad visas direkt, svaret fylls på medan det skrivs.
    const optimistic: ChatMessage = {
      id: `local_${Date.now()}`,
      role: 'student',
      content: message,
      createdAt: Math.floor(Date.now() / 1000),
    };
    setMessages((prev) => [...prev, optimistic]);

    try {
      await streamChat(lessonId, message, {
        onStudent: (m) =>
          setMessages((prev) =>
            prev.map((x) =>
              x.id === optimistic.id ? { ...x, id: m.id, createdAt: m.createdAt } : x,
            ),
          ),
        onDelta: (text) => setStreaming((prev) => prev + text),
        onDone: (m) => {
          setStreaming('');
          setMessages((prev) => [
            ...prev,
            { id: m.id, role: 'assistant', content: m.content, createdAt: m.createdAt },
          ]);
        },
      });
    } catch {
      // Elevens fråga kan ha hunnit sparas innan strömmen bröts. Hämta tråden
      // i stället för att gissa — annars riskerar ett nytt försök att skriva in
      // samma fråga två gånger.
      setStreaming('');
      setError('Svaret kom inte fram. Försök igen.');
      api
        .chat(lessonId)
        .then((view) => setMessages(view.messages))
        .catch(() => setMessages((prev) => prev.filter((m) => m.id !== optimistic.id)));
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="chat" aria-label="Chatt om lektionen">
      <header className="chat__head">
        <IconChat size={15} />
        <span className="chat__title">Fastnat? Fråga här</span>
      </header>

      <div className="chat__thread">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`bubble bubble--${message.role === 'student' ? 'student' : 'assistant'}`}
          >
            {message.content}
          </div>
        ))}
        {streaming && (
          <div className="bubble bubble--assistant bubble--streaming">{streaming}</div>
        )}
        {sending && !streaming && (
          <div className="bubble bubble--assistant bubble--pending">Tänker…</div>
        )}
        <div ref={endRef} />
      </div>

      <p className="chat__note">Chatten ger ledtrådar, inte färdiga svar.</p>

      {suggestions.length > 0 && (
        <div className="chiprow">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              className="chip chip--accent chat__suggestion"
              onClick={() => void send(s)}
              disabled={sending}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {error && <p className="chat__error">{error}</p>}

      <form
        className="chat__form"
        onSubmit={(e) => {
          e.preventDefault();
          void send(draft);
        }}
      >
        <label className="visually-hidden" htmlFor="chat-input">
          Din fråga
        </label>
        <input
          id="chat-input"
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Skriv din fråga…"
          disabled={sending}
        />
        <button type="submit" className="chat__send" aria-label="Skicka" disabled={sending}>
          <IconSend />
        </button>
      </form>
    </section>
  );
}
